/**
 * Colors tab (screenshots/78): Single · Highlight · By category (max 3) · Rules, the theme's palette (fixed-meaning colors
 * shown but not editable here), and the Accessibility list from `colorChecks()` (contrast + Machado simulation).
 * Hover previews never enter history; picking applies one undoable step.
 */
import { useMemo } from "react";
import { CIcon } from "../icons";
import { Dropdown } from "../../../components/shared/Dropdown";
import { colorChecks, resolveColor, type ColorRef, type ColorSettings } from "../chartSettings";
import { readPalette, type Palette } from "../chartOptions";
import type { Shape } from "../chartTypes";
import { useChartSettings, type ChartSettingsStore } from "../chartSettingsStore";
import { paletteFromTheme, type ChartTheme } from "../themes";
import { Switch } from "../../../components/shared/ui";
import { ColorPicker, refName } from "./ColorPicker";
import { labelRows } from "./LabelsTab";
import { Row, Section, Seg } from "./ui";

export const paletteOf = (theme?: ChartTheme): Palette => (theme ? paletteFromTheme(readPalette(), theme) : readPalette());
const OPS = [">=", ">", "<=", "<", "="] as const;
const OP_TEXT: Record<(typeof OPS)[number], string> = { ">=": "≥", ">": ">", "<=": "≤", "<": "<", "=": "=" };

export function ColorsTab({ store, shape, theme, onOpenThemes }: { store: ChartSettingsStore; shape: Shape; theme?: ChartTheme; onOpenThemes: () => void }) {
  const c = useChartSettings(store, (x) => x.settings.colors);
  const labelsOn = useChartSettings(store, (x) => x.settings.labels.dataLabels.show);
  const palette = useMemo(() => paletteOf(theme), [theme]);
  const set = (next: ColorSettings, label: string) => store.getState().update((s) => ({ ...s, colors: next }), label);
  const preview = (next: ColorSettings | null) => store.getState().setPreview(next ? { colors: next } : null);
  const rows = labelRows(shape);
  const checks = colorChecks(c, palette, labelsOn || (c.mode === "highlight" && c.highlight.label));
  const money = "unit" in shape && shape.unit === "usd";
  const sw = (r: ColorRef) => <span className="cs-sw" style={{ background: resolveColor(r, palette) }} />;
  const name = (key?: string) => rows.find((r) => r.key === key)?.label;

  return (
    <div className="cs-sections">
      <Section title="Color by">
        <Seg wide label="Color by" value={c.mode} onChange={(mode) => set({ ...c, mode, highlight: mode === "highlight" && !c.highlight.key && rows[0] ? { ...c.highlight, key: rows[0].key } : c.highlight }, `Colors: ${mode}`)}
          options={[{ id: "single", label: "Single" }, { id: "highlight", label: "Highlight" }, { id: "category", label: "By category" }, { id: "rules", label: "Rules" }]} />
      </Section>

      {c.mode === "single" && (
        <Section title="Color">
          <Row label="Bars"><ColorPicker value={c.single} palette={palette} title="Chart color" triggerClass="cs-select" onSaveToTheme={onOpenThemes}
            onHover={(r) => preview(r ? { ...c, single: r } : null)} onPick={(r) => set({ ...c, single: r }, "Color")}>{sw(c.single)}{refName(c.single)}<CIcon name="chevron-down" className="cs-select__chev" /></ColorPicker></Row>
        </Section>
      )}

      {c.mode === "highlight" && (
        <Section title="Highlight">
          <Row label="Emphasize">
            <div className="cs-select cs-select--split">
              <ColorPicker value={c.highlight.color} palette={palette} title="Highlight color" against={{ ref: c.highlight.others, name: refName(c.highlight.others) }} onSaveToTheme={onOpenThemes}
                onHover={(r) => preview(r ? { ...c, highlight: { ...c.highlight, color: r } } : null)} onPick={(r) => set({ ...c, highlight: { ...c.highlight, color: r } }, "Highlight color")} />
              <Dropdown className="cs-select__split" label="Emphasize" value={c.highlight.key ?? ""} onChange={(v) => set({ ...c, highlight: { ...c.highlight, key: v || undefined } }, `Highlight: ${name(v) ?? ""}`)}
                options={[{ value: "", label: name(c.highlight.key) ? "" : "—" }, ...rows.map((r) => ({ value: r.key, label: r.label }))]} chevron={<CIcon name="chevron-down" className="cs-select__chev" />} />
            </div>
          </Row>
          <Row label="Others">
            <ColorPicker value={c.highlight.others} palette={palette} title="Others color" against={{ ref: c.highlight.color, name: "the highlight" }} triggerClass="cs-select" onSaveToTheme={onOpenThemes}
              onHover={(r) => preview(r ? { ...c, highlight: { ...c.highlight, others: r } } : null)} onPick={(r) => set({ ...c, highlight: { ...c.highlight, others: r } }, "Others color")}>
              {sw(c.highlight.others)}{refName(c.highlight.others)}<CIcon name="chevron-down" className="cs-select__chev" /></ColorPicker>
          </Row>
          <div className="cs-row"><Switch on={c.highlight.label} label="Label the highlighted bar (so color isn’t the only cue)" onChange={(label) => set({ ...c, highlight: { ...c.highlight, label } }, "Highlight label")} />
            <span className="cs-row__text">Label the highlighted bar (so color isn’t the only cue)</span></div>
        </Section>
      )}

      {c.mode === "category" && (
        <Section title="By category">
          {rows.slice(0, 3).map((r, i) => (
            <Row key={r.key} label={r.label}>
              <ColorPicker value={c.byCategory[r.key] ?? `chart-${i + 1}`} palette={palette} title={`${r.label} color`} triggerClass="cs-select" onSaveToTheme={onOpenThemes}
                onHover={(ref) => preview(ref ? { ...c, byCategory: { ...c.byCategory, [r.key]: ref } } : null)}
                onPick={(ref) => set({ ...c, byCategory: { ...c.byCategory, [r.key]: ref } }, `${r.label} color`)}>
                {sw(c.byCategory[r.key] ?? `chart-${i + 1}`)}{refName(c.byCategory[r.key] ?? `chart-${i + 1}`)}<CIcon name="chevron-down" className="cs-select__chev" /></ColorPicker>
            </Row>
          ))}
        </Section>
      )}

      <Section title={<>Theme palette · {theme?.name ?? "Civic Light"}</>}>
        <Row label="Categories"><span className="cs-swatches">{[palette.c1, palette.c2, palette.c3, palette.muted].map((x, i) => <i key={i} style={{ background: x }} />)}</span></Row>
        <Row label="Scale"><span className="cs-swatches">{palette.seq.map((x, i) => <i key={i} style={{ background: x }} />)}</span></Row>
        <Row label="Status"><span className="cs-swatches">{[palette.positive, palette.negative, "var(--warning)"].map((x, i) => <i key={i} style={{ background: x }} />)}</span></Row>
        <Row label="Forecast"><span className="cs-swatches">{[palette.forecastFor, palette.tossup, palette.forecastAgainst].map((x, i) => <i key={i} style={{ background: x }} />)}</span></Row>
        <button type="button" className="link-button cs-edit-theme" onClick={onOpenThemes}><CIcon name="palette" size={13} />Edit this theme or create a new one</button>
      </Section>

      <Section title={<span className={c.mode === "rules" ? undefined : "is-dim"}>Rules · used in Rules mode</span>}>
        {(c.rules.length ? c.rules : [{ op: ">=" as const, value: money ? 5000 : 10, color: "chart-1" }]).map((r, i) => (
          <div key={i} className={`cs-rule${c.mode === "rules" ? "" : " is-dim"}`}>
            <span className="cs-rule__if">If</span>
            <span className="cs-rule__cond">value
              <Dropdown className="cs-inline-dd" label="Comparison" size="sm" value={r.op} disabled={c.mode !== "rules"} onChange={(op) => set({ ...c, rules: rulesWith(c, i, { op }) }, "Rule")}
                options={OPS.map((o) => ({ value: o, label: OP_TEXT[o] }))} chevron={<CIcon name="chevron-down" size={12} />} />
              <input aria-label="Rule value" disabled={c.mode !== "rules"} defaultValue={money ? `$${r.value.toLocaleString("en-US")}` : String(r.value)}
                onBlur={(e) => { const v = Number(e.target.value.replace(/[^0-9.-]/g, "")); if (!Number.isNaN(v) && v !== r.value) set({ ...c, rules: rulesWith(c, i, { value: v }) }, "Rule value"); }} /></span>
            <CIcon name="arrow-right" className="cs-rule__arrow" />
            <ColorPicker value={r.color} palette={palette} title="Rule color" triggerClass="cs-rule__color" onPick={(ref) => set({ ...c, rules: rulesWith(c, i, { color: ref }) }, "Rule color")}>
              {sw(r.color)}{refName(r.color)}</ColorPicker>
          </div>
        ))}
        <div className={`cs-rule${c.mode === "rules" ? "" : " is-dim"}`}><span className="cs-rule__if">Else</span><span className="cs-rule__cond" />
          <CIcon name="arrow-right" className="cs-rule__arrow" />
          <ColorPicker value={c.elseColor} palette={palette} title="Else color" triggerClass="cs-rule__color" onPick={(ref) => set({ ...c, elseColor: ref }, "Else color")}>{sw(c.elseColor)}{refName(c.elseColor)}</ColorPicker>
        </div>
        {c.mode === "rules" && <button type="button" className="cs-pill" onClick={() => set({ ...c, rules: [...(c.rules.length ? c.rules : rulesWith(c, 0, {})), { op: ">=", value: 0, color: "chart-2" }] }, "Rule added")}><CIcon name="plus" />Rule</button>}
      </Section>

      <Section title="Accessibility">
        <ul className="cs-checks">{checks.map((k) => (
          <li key={k.id} className={`is-${k.state}`}><CIcon name={k.state === "pass" ? "circle-check" : k.state === "warn" ? "triangle-alert" : "circle-x"} size={13} />{k.id === "muted" ? `${k.label}: ` : ""}{k.detail}</li>))}</ul>
      </Section>
    </div>
  );
}

/** The rules list with one rule patched (the placeholder rule becomes real on first edit). */
function rulesWith(c: ColorSettings, i: number, patch: Partial<ColorSettings["rules"][number]>): ColorSettings["rules"] {
  const base = c.rules.length ? c.rules : [{ op: ">=" as const, value: 5000, color: "chart-1" }];
  return base.map((r, j) => (j === i ? { ...r, ...patch } : r));
}
